<template>
  <div class="dropdown-wrapper" ref="root">
    <label for="label">{{ label }}</label>
    <div
      :class="`dropdown-input${show ? ' active' : ''}`"
      @click="toggleDropdown"
    >
      <span :class="{ 'is-placeholder': !selectedLabel }">{{ selectedLabel || resolvedPlaceholder }}</span>
      <img class="next-icon" src="@/assets/icons/next.svg" alt="next">
    </div>
    <div v-show="show" class="dropdown-items-wrapper">
      <!-- Never render an empty (invisible) menu: always show loading/empty state
           so a slow or failed options fetch is visible, not a silent void. -->
      <div v-if="loading" class="dropdown-message">
        {{ resolvedLoadingText }}
      </div>
      <div
        v-else-if="!items || items.length === 0"
        :class="`dropdown-message${hasRetryListener ? ' dropdown-message-action' : ''}`"
        @click.stop="onEmptyClick"
      >
        {{ resolvedEmptyText }}
      </div>
      <template v-else>
        <div
          v-for="(item, index) in items"
          :key="index"
          :class="`dropdown-item${item?.value === modelValue?.value ? ' active' : ''}`"
          @click.stop="onItemSelect(item)"
        >
          {{ item.label }}
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onBeforeUnmount, getCurrentInstance } from 'vue'
import { useI18n } from 'vue-i18n'

export default {
  props: {
    type: {
      default: 'text',
    },
    modelValue: {
      require: true,
    },
    label: {
      default: '',
    },
    name: {
      require: true,
    },
    items: {
      require: true,
      type: Array,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    placeholder: {
      type: String,
      default: '',
    },
    loadingText: {
      type: String,
      default: '',
    },
    emptyText: {
      type: String,
      default: '',
    },
  },
  emits: ['update:modelValue', 'retry'],
  setup(props, { emit }) {
    /** context */
    const { t } = useI18n()
    // `retry` is a declared emit, so Vue strips its listener out of $attrs — the
    // parent's raw vnode props are the only place left to see whether one is bound.
    const instance = getCurrentInstance()

    /** reactive */
    const show = ref(false)
    const root = ref(null)

    /** computed */
    const selectedLabel = computed(() => {
      const found = props.items?.find(item => item.value === props?.modelValue?.value)
      return found ? found.label : null
    })

    // Only the caller knows whether re-fetching is possible, so the empty state is
    // offered as a tappable retry only when a @retry listener is actually bound.
    // Without this, address/province dropdowns would invite a tap that does nothing.
    const hasRetryListener = computed(() => !!instance?.vnode?.props?.onRetry)

    // The closed control and the loading/empty states must never render blank: a
    // caller that omits these props would otherwise produce an invisible menu that
    // looks like a dead button. Fall back to the shared strings instead.
    const resolvedPlaceholder = computed(() => props.placeholder || t('selectPlaceholder'))
    const resolvedLoadingText = computed(() => props.loadingText || t('loadingOptions'))
    const resolvedEmptyText = computed(() => {
      if (props.emptyText) return props.emptyText
      return hasRetryListener.value ? t('noOptionsRetry') : t('noOptions')
    })

    /** method */
    const toggleDropdown = () => {
      show.value = !show.value
    }
    const onItemSelect = (item) => {
      emit('update:modelValue', item)
      show.value = false // close on select instead of relying on a second tap
    }
    const onEmptyClick = () => {
      // Let the parent re-fetch options when the list is empty/failed.
      if (hasRetryListener.value) emit('retry')
    }

    // Close when tapping outside. Fixes menus that appear "stuck open" and makes
    // behaviour predictable across Android in-app browsers.
    const handleDocumentClick = (event) => {
      if (show.value && root.value && !root.value.contains(event.target)) {
        show.value = false
      }
    }
    onMounted(() => document.addEventListener('click', handleDocumentClick))
    onBeforeUnmount(() => document.removeEventListener('click', handleDocumentClick))

    return {
      root,
      show,
      selectedLabel,
      hasRetryListener,
      resolvedPlaceholder,
      resolvedLoadingText,
      resolvedEmptyText,
      onItemSelect,
      onEmptyClick,
      toggleDropdown,
    }
  },
}
</script>

<style lang="scss" scoped>

.dropdown-wrapper {
  display: flex;
  flex-direction: column;
  margin-bottom: 18px;
  position: relative;
}

label {
  color: #6A6A6A;
  margin-bottom: 12px;
}

.dropdown-input {
  border: 1px solid #DFDFDF;
  background-color: #fff;
  padding: 8px 12px;
  border-radius: 8px;
  outline: none;
  display: flex;
  align-items: center;
  cursor: pointer;
  min-height: 40px;
  &.active {
    border: 1px solid var(--secondary-color);
  }
  .is-placeholder {
    color: #9A9A9A;
  }
}

.dropdown-items-wrapper {
  top: calc(100% + 4px);
  position: absolute;
  left: 0;
  right: 0;
  max-height: 300px;
  overflow-y: auto;
  background-color: var(--white-color);
  z-index: 1000;
  border: 1px solid var(--secondary-color);
  border-radius: 8px;
  box-shadow: 0px 4px 16px rgba(0, 0, 0, 0.16);
}

.dropdown-message {
  background-color: var(--white-color);
  color: #6A6A6A;
  padding: 12px;
  border-radius: 8px;
  text-align: center;
}
.dropdown-message-action {
  cursor: pointer;
  color: var(--secondary-color);
}

.dropdown-item {
  background-color: var(--white-color);
  border-top: 1px solid var(--secondary-color);
  border-bottom: 1px solid var(--secondary-color);
  cursor: pointer;
  padding: 8px 12px;
  &.active {
    background-color: var(--secondary-color);
    color: var(--white-color);
  }
  &:first-child {
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
  }
  &:last-child {
    border-bottom-left-radius: 8px;
    border-bottom-right-radius: 8px;
  }
  &:not(:last-child) {
    border-bottom: 0;
  }
}

.next-icon {
  margin-left: auto;
}
</style>
